import { useState } from 'react';
import { useNewSizingNarrative } from '@/hooks/useNewSizingNarrative';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
import { InfrastructureSummaryTab } from '@/components/outputs/tabs/InfrastructureSummaryTab';
import { PersonaUsageModelingTab } from '@/components/outputs/tabs/PersonaUsageModelingTab';
import { MethodsFormulasTab } from '@/components/outputs/tabs/MethodsFormulasTab';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Activity, Layers } from 'lucide-react';
import { formatNumber } from '@/components/sizing/advancedInsightsShared';
type NarrativePanelsProps = {
  snapshot: NewSizingSnapshot | null;
  modelName?: string;
  serverSku?: string;
  serverDisplayName?: string;
  gpusPerServer?: number;
  hbmBytesPerGpu?: number;
  weightsGiB?: number;
  weightsGiBUnshared?: number;
  runtimeOverheadGiB?: number;
  usableKvGiBPerGpu?: number;
  bridgeGroupSize?: number;
  conversationTurns?: number;
  latencyMs?: number;
  batchSize?: number;
  personas?: Array<{
    id: string;
    name: string;
    sharePercent: number;
    population: number;
    inputTokensPerConversation: number;
    outputTokensPerConversation: number;
  }>;
  isComputing?: boolean;
};
export const NarrativePanels: React.FC<NarrativePanelsProps> = props => {
  const {
    snapshot,
    modelName,
    serverSku,
    serverDisplayName,
    gpusPerServer,
    hbmBytesPerGpu,
    weightsGiB,
    weightsGiBUnshared,
    runtimeOverheadGiB,
    usableKvGiBPerGpu,
    bridgeGroupSize,
    conversationTurns,
    latencyMs,
    batchSize,
    personas,
    isComputing
  } = props;
  const narrativeData = useNewSizingNarrative({
    snapshot,
    modelName,
    serverSku,
    serverDisplayName,
    gpusPerServer,
    hbmBytesPerGpu,
    weightsGiB,
    weightsGiBUnshared,
    runtimeOverheadGiB,
    usableKvGiBPerGpu,
    bridgeGroupSize,
    conversationTurns,
    latencyMs,
    batchSize,
    personas
  });
  const isLoading = isComputing || !narrativeData;
  if (isLoading) {
    return <div className="space-y-4">
        {Array.from({
        length: 6
      }).map((_, index) => <Card key={index}>
            <CardHeader>
              <Skeleton className="h-5 w-48" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (!narrativeData) {
    return null;
  }
  const [accordionValue, setAccordionValue] = useState<string>('');
  return <div className="space-y-6">
      {/* High-Level KPI Cards */}
      

      {/* Advanced Insights Accordion */}
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="advanced-insights" className="border border-border rounded-lg">
          <AccordionTrigger className="px-6 hover:no-underline">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              <span className="text-lg font-semibold">Advanced Insights & Analytics</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <Tabs defaultValue="infrastructure" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="infrastructure" className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Infrastructure Summary
                </TabsTrigger>
                <TabsTrigger value="persona" className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Persona & Usage Modeling
                </TabsTrigger>
                <TabsTrigger value="methods" className="flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Methods & Formulas
                </TabsTrigger>
              </TabsList>

              <TabsContent value="infrastructure" className="mt-0">
                <InfrastructureSummaryTab data={narrativeData} />
              </TabsContent>

              <TabsContent value="persona" className="mt-0">
                <PersonaUsageModelingTab data={narrativeData} />
              </TabsContent>

              <TabsContent value="methods" className="mt-0">
                <MethodsFormulasTab data={narrativeData} />
              </TabsContent>
            </Tabs>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>;
};

// import { useSizingNarrativeData } from '@/hooks/useSizingNarrativeData';
// import type { PaperTrail } from '@/types/paperTrail';
// import type { EstimateResponse, PerGpuMemorySummary, ScenarioState } from '@/types';
// import type { DemandPercentiles } from '@/types/percentiles';
// import type { McMemorySnapshot } from '@/types/sizing.mc';
// import { ErrorPanel } from '@/components/outputs/ErrorPanel';
// import { PopulationDutyCyclePanel } from '@/components/outputs/PopulationDutyCyclePanel';
// import { ThroughputMemoryPanel } from '@/components/outputs/ThroughputMemoryPanel';
// import { FinalHardwarePlanPanel } from '@/components/outputs/FinalHardwarePlanPanel';
// import { InfrastructureMethodsPanel } from '@/components/outputs/InfrastructureMethodsPanel';
// import { Skeleton } from '@/components/ui/skeleton';
// import { Card, CardContent, CardHeader } from '@/components/ui/card';

// import type { CatalogData } from '@/hooks/useCatalog';

// type NarrativePanelsProps = {
//   trail: PaperTrail;
//   scenario: ScenarioState;
//   mcActives?: Record<string, number> | null;
//   effectivePromptTpsPerGpu?: number;
//   effectiveDecodeTpsPerGpu?: number;
//   refineActives?: Record<string, number> | null;
//   previewActives?: Record<string, number> | null;
//   baseConcurrentUsers: number;
//   memoryPerGpu?: PerGpuMemorySummary | null;
//   liveMemorySnapshot?: McMemorySnapshot | null;
//   kvOutputs?: EstimateResponse['kv'] | null;
//   estimate?: EstimateResponse | null;
//   userConcurrentUsersInput: number;
//   percentiles?: DemandPercentiles;
//   mcActiveSummary?: { p5?: number; p25?: number; p50?: number; p75?: number; p95?: number; p99?: number };
//   mcKvSummary?: { p5?: number; p25?: number; p50?: number; p75?: number; p95?: number; p99?: number };
//   mcScale?: number;
//   mcAnchorPercentile?: 'p50' | 'p75';
//   gpusThroughput?: number;
//   gpusKv?: number;
//   gpusWeights?: number;
//   finalGpuCount?: number;
//   finalServerCount?: number;
//   catalog?: CatalogData;
// };

// export const NarrativePanels = ({
//   trail,
//   scenario,
//   mcActives,
//   effectivePromptTpsPerGpu,
//   effectiveDecodeTpsPerGpu,
//   refineActives,
//   previewActives,
//   baseConcurrentUsers,
//   memoryPerGpu,
//   liveMemorySnapshot,
//   kvOutputs,
//   estimate,
//   userConcurrentUsersInput,
//   percentiles,
//   mcActiveSummary,
//   mcKvSummary,
//   mcScale,
//   mcAnchorPercentile,
//   gpusThroughput,
//   gpusKv,
//   gpusWeights,
//   finalGpuCount,
//   finalServerCount,
//   catalog,
// }: NarrativePanelsProps) => {

//   // Use the new hook to gather all data
//   const data = useSizingNarrativeData({
//     estimate,
//     scenario,
//     userConcurrentUsersInput,
//     mcActiveP50: mcActiveSummary?.p50,
//     mcActiveP95: mcActiveSummary?.p95,
//     mcActiveByPersona: mcActives ?? undefined,
//     catalog,
//   });

//   // Show loading skeletons while data is being computed
//   if (!data) {
//     return (
//       <div className="space-y-8">
//         {[1, 2, 3, 4].map((i) => (
//           <Card key={i} className="rounded-2xl border border-border bg-card shadow-sm">
//             <CardHeader>
//               <Skeleton className="h-6 w-64 mb-2" />
//               <Skeleton className="h-4 w-full max-w-2xl" />
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <Skeleton className="h-24 w-full" />
//               <div className="grid grid-cols-2 gap-4">
//                 <Skeleton className="h-32 w-full" />
//                 <Skeleton className="h-32 w-full" />
//               </div>
//             </CardContent>
//           </Card>
//         ))}
//       </div>
//     );
//   }

//   if (estimate?.desError) {
//     return (
//       <ErrorPanel
//         title="Simulation Error"
//         message="The discrete-event simulation exceeded safe runtime and was aborted. Try reducing trials or horizon length."
//       />
//     );
//   }

//   // Construct metrics object for InfrastructureMethodsPanel
//   // This adapts the new data structure to what the panel expects
//   const methodsMetrics = {
//     workload: data.workload,
//     requests: {
//       aggregate: data.requests,
//       profiles: data.personas.map(p => ({
//         id: p.id,
//         label: p.label,
//         normalizedSharePct: p.sharePct,
//         concurrency: p.population, // Using population as concurrency for profile view
//         totalTokensPerConversation: p.tokensPerConversation,
//       }))
//     },
//     server: {
//       sku: data.serverConfig.sku,
//       displayName: data.serverConfig.displayName,
//       acceleratorsPerServer: data.serverConfig.acceleratorsPerServer,
//       throughputHeadroomPct: data.serverConfig.throughputHeadroomPct,
//       memoryHeadroomPct: data.serverConfig.memoryHeadroomPct,
//       throughputPerGpu: {
//         promptTokensPerSecond: data.serverConfig.promptTpsPerGpu,
//         decodeTokensPerSecond: data.serverConfig.decodeTpsPerGpu,
//         estimated: data.serverConfig.throughputEstimated,
//       },
//       throughputPerServer: {
//         promptTokensPerSecond: data.serverConfig.promptTpsPerServer,
//         decodeTokensPerSecond: data.serverConfig.decodeTpsPerServer,
//       },
//       memory: {
//         hbmBytesPerGpu: data.serverConfig.hbmBytesPerGpu,
//         usableBytesPerGpu: data.serverConfig.usableBytesPerGpu,
//         averageUsedBytesPerGpu: data.serverConfig.averageUsedBytesPerGpu,
//         peakGpuUtilizationPct: data.serverConfig.peakGpuUtilizationPct,
//         averageUsedBytesPerServer: data.serverConfig.averageUsedBytesPerServer,
//         usableBytesPerServer: data.serverConfig.usableBytesPerServer,
//       }
//     },
//     fleet: {
//       totalServers: data.fleetCapacity.totalServers,
//       totalGpus: data.fleetCapacity.totalGpus,
//       gpusPerServer: data.finalPlan.gpusPerServer,
//       usersPerServer: data.fleetCapacity.usersPerServer,
//       totalUsersCapacity: data.fleetCapacity.totalUsersCapacity,
//       memoryUtilizationPct: data.fleetCapacity.memoryUtilizationPct,
//     }
//   };

//   return (
//     <div className="space-y-8">
//       <PopulationDutyCyclePanel data={data} />
//       <ThroughputMemoryPanel data={data} />
//       <FinalHardwarePlanPanel data={data} />
//       <InfrastructureMethodsPanel metrics={methodsMetrics} />
//     </div>
//   );
// };